import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import NewsletterForm from "@/components/feature/NewsletterForm";
import { categories, site } from "@/mocks/portal";
import { usePageMeta, injectJsonLd, clearPageJsonLd } from "@/hooks/usePageMeta";

const values = [
  { icon: "ri-leaf-line", title: "真实优先", text: "先核实，再传播。所有选题与事实表述都以可查证、可追溯为底线，不追逐未经核实的消息。" },
  { icon: "ri-focus-3-line", title: "克制表达", text: "不靠标题哗众取宠，不以制造对立与焦虑为代价。好内容应该让人平静，而不是让人恐慌。" },
  { icon: "ri-community-line", title: "尊重他人", text: "不消费、不冒犯具体个人。报道与人相关的议题时，始终把善意与边界放在传播热度之前。" },
  { icon: "ri-heart-line", title: "认真生活", text: "我们相信内容最终服务于更好的生活：一部好剧、一首歌、一顿饭、一次漫游，都值得被认真对待。" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 看料站 · 内容理念与读者口碑",
    description:
      "了解看料站：我们是一个专注影视、音乐、生活方式与文化社会话题的内容聚合门户，坚持真实、克制、尊重与认真生活的内容理念，欢迎了解我们的定位与读者口碑。",
    keywords: "看料站,关于我们,内容理念,内容原则,看料站介绍",
    path: "/about",
  });

  useEffect(() => {
    const json = {
      "@type": "AboutPage",
      name: "关于看料站",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: site.name,
        description: "影视、音乐、生活方式与文化社会话题的内容聚合阅读门户。",
        email: site.contact.email,
        contactPoint: { "@type": "ContactPoint", contactType: "customer support", email: site.contact.email, inLanguage: "zh-CN" },
      },
    };
    const el = injectJsonLd(json);
    return () => clearPageJsonLd();
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background-50 text-foreground-900">
      <SiteHeader />

      <main className="flex-1">
        {/* 关于头部 */}
        <section className="relative overflow-hidden bg-primary-700 text-background-50">
          <div className="pointer-events-none absolute inset-0 opacity-20 [background:radial-gradient(circle_at_1px_1px,oklch(var(--background-50))_1px,transparent_0)] [background-size:22px_22px]" />
          <div className="relative mx-auto w-full max-w-[820px] px-4 py-14 text-center md:px-6 md:py-20">
            <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium">
              <i className="ri-flag-2-line" /> 关于我们
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black leading-tight md:text-5xl">
              热门内容，一站看全
            </h1>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-background-50/80">
              {site.name}是一个面向普通读者的内容聚合阅读门户。我们相信，在信息过载的时代，真正稀缺的不是更多，而是更值得。
            </p>
          </div>
        </section>

        <div className="mx-auto w-full max-w-[1180px] px-4 py-12 md:px-6 md:py-16">
          {/* 品牌介绍与定位 */}
          <section className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">Who We Are</span>
              <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们为什么做看料站</h2>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700">
                每天有海量内容在生成，也有大量时间被无声浪费。很多读者不是不想看好内容，而是不知道去哪找、该信什么。{site.name}想做的，正是这件事：把分散在各个角落的优质内容，用清晰栏目与认真筛选整理出来，帮你在有限时间里，读到真正值得的那一篇。
              </p>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700">
                我们覆盖影视、音乐现场、生活方式与文化社会四个方向，内容既有轻松的片单与食谱，也有值得慢读的观察与讨论。我们不做流量收割的标题党，也不做针对个人隐私的“扒料”，只想安静地把好内容送到需要的人面前。
              </p>
            </div>
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-7">
              <h3 className="font-heading text-lg font-bold text-foreground-950">我们服务的读者</h3>
              <ul className="mt-4 space-y-3">
                {[
                  "下班后想找一部好剧、一份好片单的追剧党",
                  "想了解现场演出、独立音乐文化的声音爱好者",
                  "想把居家、饮食与周末过出质感的认真生活家",
                  "对文化现象与社会议题保持好奇、愿意慢读的读者",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-fill mt-0.5 text-accent-600" />
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-5 rounded-lg bg-accent-50 p-4">
                <p className="flex items-center gap-2 text-sm font-medium text-accent-800">
                  <i className="ri-quill-pen-line" /> 我们的内容原则
                </p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                  以读者时间为尊，以真实与尊重为纲。愿意花时间做筛选和核实，把最值得的那一篇，放在最显眼的位置。
                </p>
              </div>
            </div>
          </section>

          {/* 内容原则卡片 */}
          <section id="principles" className="mt-16 scroll-mt-24" aria-label="内容原则">
            <div className="text-center">
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary-600">Principles</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们的内容原则</h2>
            </div>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-100 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${v.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 栏目一览 */}
          <section className="mt-16" aria-label="栏目一览">
            <div className="text-center">
              <span className="text-xs font-semibold uppercase tracking-widest text-accent-700">Columns</span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">四个栏目，四个入口</h2>
            </div>
            <div className="mt-8 grid gap-5 md:grid-cols-2">
              {categories.map((c) => (
                <Link key={c.slug} to={`/column/${c.slug}`} className="group flex items-center gap-5 rounded-lg border border-background-200/70 bg-background-100 p-6 transition-colors hover:border-primary-200 hover:bg-primary-50">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                    <i className={`${c.slug === "yingshi" ? "ri-film-line" : c.slug === "yinyue" ? "ri-music-2-line" : c.slug === "shenghuo" ? "ri-cup-line" : "ri-earth-line"} text-2xl`} />
                  </span>
                  <span className="flex-1">
                    <span className="font-heading text-xl font-bold text-foreground-950 group-hover:text-primary-700">{c.name}</span>
                    <span className="mt-1 block text-sm text-foreground-600">{c.tagline}</span>
                  </span>
                  <i className="ri-arrow-right-line text-foreground-300 transition-transform group-hover:translate-x-1 group-hover:text-primary-600" />
                </Link>
              ))}
            </div>
          </section>

          {/* 发展理念/联系 */}
          <section id="contact" className="mt-16 grid gap-10 scroll-mt-24 lg:grid-cols-2" aria-label="联系与理念">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">Contact</span>
              <h2 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-4 text-[15px] leading-8 text-foreground-700">
                无论是想合作投稿、提供优质选题，还是对某篇内容有疑问，都欢迎通过下面的方式找到我们。我们珍视每一位读者的反馈。
              </p>
              <ul className="mt-6 space-y-4">
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-xl" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">内容与商务邮箱</p>
                    <p className="text-sm font-semibold text-foreground-900">{site.contact.email}</p>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-wechat-line text-xl" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">读者群（官方号）</p>
                    <p className="text-sm font-semibold text-foreground-900">{site.contact.wechat}</p>
                  </div>
                </li>
                <li className="flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-article-line text-xl" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">反馈建议</p>
                    <p className="text-sm font-semibold text-foreground-900">随邮件附上文章链接，我们会尽快处理并回复。</p>
                  </div>
                </li>
              </ul>
              <div className="mt-7 rounded-lg border border-background-200/70 bg-background-100 p-5 text-sm text-foreground-700">
                <p className="flex items-center gap-2 font-semibold text-foreground-950">
                  <i className="ri-road-map-line text-primary-600" /> 我们的下一步
                </p>
                <p className="mt-2 leading-relaxed">
                  我们会持续扩充栏目深度、邀请更多创作者加入，并逐步推出读者社群与线下观影交流。欢迎和看料站一起，把内容做得更好。
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8" id="subscribe">
              <NewsletterForm
                id="about-contact"
                withMessage
                title="给我们留言或订阅"
                subtitle="想订阅每周精选，或有选题建议？留个言，我们会认真阅读每一条消息。"
              />
            </div>
          </section>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}